import { Injectable } from '@angular/core';
import { NavigationEnd } from '@angular/router';
import { CommandEnum } from 'src/app/models/common/enums/command.enum';
import { CommandService } from './command.service';
import {
  BookingAttribution,
  getBookingAttribution,
} from './attribution.service';

declare global {
  interface Window {
    gtag?: Function;
  }
}

export interface Ga4EventPayload {
  eventname: string;
  client_id: string;
  session_id: string | null;
  session_number: number | null;
  attribution: BookingAttribution;
  infoOrder_id: number;
  eventparams: object;
}

@Injectable({
  providedIn: 'root',
})
export class GoogleAnalyticsService {
  private static readonly id = 'G-SXT2MLBVWM';
  private static readonly clientIdStorageKey = 'ga4_client_id';
  private static instance: GoogleAnalyticsService;

  constructor(private commandService: CommandService) {
    GoogleAnalyticsService.instance = this;
    this.getClientId();
  }

  public static event(
    name: string,
    data: {
      category: string;
      label?: string;
      value?: string;
    },
  ) {
    if (!GoogleAnalyticsService.instance) {
      console.warn(
        `GA4 service is not initialized. Event "${name}" was not stored.`,
      );
      return;
    }

    GoogleAnalyticsService.instance.trackEvent(name, {
      event_category: data.category,
      event_label: data.label,
      value: data.value,
    });
  }

  public async trackEvent(
    name: string,
    eventparams: object = null,
    infoOrderId: number = null,
  ): Promise<void> {
    try {
      const [clientId, sessionId, sessionNumber] = await Promise.all([
        this.getClientId(),
        this.getSessionId(),
        this.getSessionNumber(),
      ]);
      const payload: Ga4EventPayload = {
        eventname: name,
        client_id: clientId,
        session_id: sessionId,
        session_number: sessionNumber,
        attribution: getBookingAttribution(),
        infoOrder_id: infoOrderId,
        eventparams,
      };
      console.debug(`Storing GA4 event: ${name} with data: `, payload);

      await this.commandService.executeCommandForTracking(
        CommandEnum.AddGa4Event,
        payload,
      );
    } catch (error) {
      console.warn(`AddGa4Event ${name} skipped: `, error);
    }
  }

  public getCustomerType(): 'new' | 'returning' {
    if (localStorage.getItem('first-journey') === 'true') {
      return 'new';
    }

    if (localStorage.getItem('firstName')) {
      return 'returning';
    }

    return 'new';
  }

  public static route(navEnd: NavigationEnd) {
    let route = navEnd.urlAfterRedirects.split('?')[0];
    console.debug(`Navigation event skipped for GA4 direct send: ${route}`);
  }

  public async trackBookingConfirmed(
    infoOrderId: number,
    postcode: string = null,
    slotDate: string = null,
  ): Promise<void> {
    await this.trackEvent(
      'purchase_initiated',
      {
        transaction_id: infoOrderId ? infoOrderId.toString() : null,
        currency: 'GBP',
        value: 0,
        postcode,
        slot_date: slotDate,
        customer_type: this.getCustomerType(),
      },
      infoOrderId,
    );
  }

  public async getClientId(): Promise<string> {
    const cachedClientId = localStorage.getItem(
      GoogleAnalyticsService.clientIdStorageKey,
    );
    if (cachedClientId) {
      return cachedClientId;
    }

    const gaClientId = await this.readClientIdFromGtag();
    const clientId = gaClientId || this.readClientIdFromCookie();

    if (clientId) {
      this.saveClientId(clientId);
    }

    return clientId;
  }

  private readClientIdFromGtag(): Promise<string> {
    return new Promise((resolve) => {
      if (!window.gtag) {
        resolve(null);
        return;
      }

      window.gtag(
        'get',
        GoogleAnalyticsService.id,
        'client_id',
        (clientId: string) => {
          resolve(clientId || null);
        },
      );

      setTimeout(() => resolve(null), 1500);
    });
  }

  private readClientIdFromCookie(): string {
    const gaCookie = document.cookie
      .split('; ')
      .find((cookie) => cookie.startsWith('_ga='));

    if (!gaCookie) {
      return null;
    }

    const parts = gaCookie.split('=')[1].split('.');
    if (parts.length < 4) {
      return null;
    }

    return `${parts[parts.length - 2]}.${parts[parts.length - 1]}`;
  }

  private getSessionId(): Promise<string | null> {
    return new Promise((resolve) => {
      if (window.gtag) {
        window.gtag(
          'get',
          GoogleAnalyticsService.id,
          'session_id',
          (id: string) => resolve(id || null),
        );
        setTimeout(() => resolve(null), 1500);
        return;
      }
      resolve(this.readSessionFromCookie()?.[0] ?? null);
    });
  }

  private getSessionNumber(): Promise<number | null> {
    return new Promise((resolve) => {
      if (window.gtag) {
        window.gtag(
          'get',
          GoogleAnalyticsService.id,
          'session_number',
          (n: string) => resolve(n ? parseInt(n, 10) : null),
        );
        setTimeout(() => resolve(null), 1500);
        return;
      }
      const raw = this.readSessionFromCookie()?.[1];
      resolve(raw ? parseInt(raw, 10) : null);
    });
  }

  private readSessionFromCookie(): [string, string] | null {
    const cookieId = GoogleAnalyticsService.id.replace('G-', '');
    const cookie = document.cookie
      .split('; ')
      .find((c) => c.startsWith(`_ga_${cookieId}=`));
    if (!cookie) return null;
    // format: GS1.1.{session_id}.{session_number}.{hit_count}...
    const parts = cookie.split('=')[1].split('.');
    if (parts.length < 4) return null;
    return [parts[2], parts[3]];
  }

  private saveClientId(clientId: string): void {
    localStorage.setItem(GoogleAnalyticsService.clientIdStorageKey, clientId);
    document.cookie = `${GoogleAnalyticsService.clientIdStorageKey}=${clientId}; max-age=31536000; path=/; SameSite=Lax`;
  }

  private hasBlancSession(): boolean {
    return document.cookie
      .split('; ')
      .some(
        (cookie) =>
          cookie.startsWith('blanc_session_id=') && !!cookie.split('=')[1],
      );
  }

  private static configure(params: object = null): void {
    if (!window.gtag) {
      return;
    }

    if (params) {
      window.gtag('config', GoogleAnalyticsService.id, params);
    } else {
      window.gtag('config', GoogleAnalyticsService.id);
    }
  }
}
